import { Add, ArchiveOutlined, Delete, Edit, UnarchiveOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link as MuiLink,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import CustomPagination from '../../components/Pagination';
import MaintenanceDetailsModal from '../../components/printer-maintenances/MaintenanceDetailsModal';
import ReplacementPartsOverviewCards from '../../components/replacement-parts/ReplacementPartsOverviewCards';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents, formatDate, presentUuid } from '../../helpers/formatters';
import { getPrinterBrandBadgeProps } from '../../helpers/printerBrand';
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import { filterActivePrinters, parsePrinterTypeKey, uniquePrinterTypes } from '../../helpers/printers';
import { formatStockQuantity, printerTypeLabelForPart } from '../../helpers/replacementParts';
import { useListFetch } from '../../hooks/useListFetch';

const EMPTY_STATS = {
    total_items: 0,
    total_stock_value_cents: 0,
    highest_stock: [],
    lowest_stock: [],
};

function ListReplacementPartsPage() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(EMPTY_STATS);
    const [printers, setPrinters] = useState([]);
    const [draftType, setDraftType] = useState('');
    const [draftSearch, setDraftSearch] = useState('');
    const [queryFilters, setQueryFilters] = useState({ printer_brand: '', printer_model: '', q: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [listVersion, setListVersion] = useState(0);
    const [showArchived, setShowArchived] = useState(false);
    const [archivedTotal, setArchivedTotal] = useState(0);
    const [maintenanceModal, setMaintenanceModal] = useState({ open: false, uuid: '', printerName: '' });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const printerTypes = useMemo(
        () => uniquePrinterTypes(filterActivePrinters(printers)),
        [printers],
    );

    const hasActiveFilters = Boolean(queryFilters.printer_brand || queryFilters.q);

    useEffect(() => {
        api.get('printers?page=1&limit=100')
            .then((response) => {
                setPrinters(response.data.printers || []);
            })
            .catch(() => {});
    }, []);

    const fetchItems = useCallback(() => {
        setIsLoading(true);
        api.get('replacement-parts', {
            params: {
                page: currentPage,
                limit: itemsPerPage,
                q: queryFilters.q || undefined,
                printer_brand: queryFilters.printer_brand || undefined,
                printer_model: queryFilters.printer_brand ? queryFilters.printer_model : undefined,
                ...(showArchived ? { out_of_stock: 1 } : { in_stock: 1 }),
            },
        })
            .then((response) => {
                const data = response.data.data || {};
                setItems(data.items || []);
                setTotalPages(data.total_pages || 1);
                setStats(data.stats || EMPTY_STATS);
                setArchivedTotal(data.archived_total || 0);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [
        currentPage,
        itemsPerPage,
        queryFilters.printer_brand,
        queryFilters.printer_model,
        queryFilters.q,
        showArchived,
    ]);

    useListFetch(fetchItems, [
        currentPage,
        itemsPerPage,
        listVersion,
        queryFilters.printer_brand,
        queryFilters.printer_model,
        queryFilters.q,
        showArchived,
        fetchItems,
    ]);

    const triggerReload = () => {
        setCurrentPage(1);
        setListVersion((version) => version + 1);
    };

    const handleApplyFilters = () => {
        const { brand, model } = parsePrinterTypeKey(draftType);
        setQueryFilters({
            printer_brand: brand,
            printer_model: model,
            q: draftSearch.trim(),
        });
        triggerReload();
    };

    const handleClearFilters = () => {
        setDraftType('');
        setDraftSearch('');
        setQueryFilters({ printer_brand: '', printer_model: '', q: '' });
        triggerReload();
    };

    const handleOpenMaintenance = (usage) => {
        if (!usage?.maintenance_uuid) {
            return;
        }
        setMaintenanceModal({
            open: true,
            uuid: usage.maintenance_uuid,
            printerName: usage.printer_name || '',
        });
    };

    const handleCloseMaintenance = () => {
        setMaintenanceModal({ open: false, uuid: '', printerName: '' });
    };

    const handleDelete = (partId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta peça será removida permanentemente do estoque!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? 'Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
        }).then((result) => {
            if (result.isConfirmed && !loadingDelete) {
                setLoadingDelete(true);
                api.delete(`replacement-parts/${partId}`)
                    .then(() => {
                        setItems((prev) => prev.filter((item) => item.id !== partId));
                        setListVersion((version) => version + 1);
                        Swal.fire('Removido!', 'A peça foi removida com sucesso.', 'success');
                    })
                    .catch(() => {
                        Swal.fire('Erro!', 'Houve um problema ao remover a peça.', 'error');
                    })
                    .finally(() => {
                        setLoadingDelete(false);
                    });
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Peças de reposição</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: 2,
            }}>
                <Typography variant="h4" gutterBottom>
                    Peças de reposição
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
                    <PageTutorialButtons pageKey="replacement_parts" fullWidth={isMobile} />
                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={showArchived ? <UnarchiveOutlined /> : <ArchiveOutlined />}
                        onClick={() => {
                            setShowArchived((prev) => !prev);
                            setCurrentPage(1);
                        }}
                        fullWidth={isMobile}
                    >
                        {showArchived
                            ? 'Ver ativos'
                            : `Ver arquivados${archivedTotal > 0 ? ` (${archivedTotal})` : ''}`}
                    </Button>
                    <QuotaLimitedButton
                        quotaKey="replacement_parts"
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/admin/register-replacement-part')}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Peça
                    </QuotaLimitedButton>
                </Box>
            </Box>

            {!showArchived && <ReplacementPartsOverviewCards stats={stats} />}

            {showArchived && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Estas peças estão com estoque zerado: não aparecem na listagem principal nem nas opções
                    ao registrar uma manutenção. Edite a quantidade se voltarem ao estoque.
                </Alert>
            )}

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Filtros
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-printer-type-label">Impressora</InputLabel>
                            <Select
                                labelId="filter-printer-type-label"
                                label="Impressora"
                                value={draftType}
                                onChange={(event) => setDraftType(event.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {printerTypes.map((type) => (
                                    <MenuItem key={type.key} value={type.key}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Buscar por nome"
                            placeholder="Nome da peça"
                            value={draftSearch}
                            onChange={(event) => setDraftSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    handleApplyFilters();
                                }
                            }}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Button variant="contained" onClick={handleApplyFilters}>
                        Filtrar
                    </Button>
                    {hasActiveFilters && (
                        <Button variant="outlined" color="secondary" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                            Limpar filtros
                        </Button>
                    )}
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Peça</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Impressora</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Valor unitário</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Valor em estoque</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(items) && items.length > 0 ? items.map((item) => {
                            const typeLabel = printerTypeLabelForPart(item);
                            const brandBadge = item.printer_brand ? getPrinterBrandBadgeProps(item.printer_brand) : null;
                            const usages = Array.isArray(item.usages) ? item.usages : [];
                            return (
                                <TableRow key={item.id} sx={{ verticalAlign: 'top' }}>
                                    <TableCell>
                                        <Typography>{item.name}</Typography>
                                        {usages.length > 0 && (
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    pl: 1.5,
                                                    borderLeft: 2,
                                                    borderColor: 'primary.light',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                                                >
                                                    {usages.length === 1 ? '1 uso nesta peça' : `${usages.length} usos nesta peça`}
                                                </Typography>
                                                {usages.map((usage, usageIndex) => (
                                                    <Typography
                                                        key={`${item.id}-usage-${usageIndex}`}
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ display: 'block', mb: 0.25 }}
                                                    >
                                                        {formatDate(usage.used_at, false)}
                                                        {usage.printer_name ? ` · ${usage.printer_name}` : ''}
                                                        {` · ${usage.quantity || 1} un.`}
                                                        {usage.maintenance_uuid ? (
                                                            <>
                                                                {' · '}
                                                                <MuiLink
                                                                    component="button"
                                                                    type="button"
                                                                    onClick={() => handleOpenMaintenance(usage)}
                                                                    underline="hover"
                                                                    sx={{ cursor: 'pointer', font: 'inherit' }}
                                                                >
                                                                    {presentUuid(usage.maintenance_uuid)}
                                                                </MuiLink>
                                                            </>
                                                        ) : null}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {item.applies_to_all_printers || !item.printer_brand ? (
                                            <Chip label="Todas" size="small" />
                                        ) : brandBadge ? (
                                            <Chip label={typeLabel} size="small" {...brandBadge} />
                                        ) : (
                                            typeLabel
                                        )}
                                    </TableCell>
                                    <TableCell>{formatStockQuantity(item.quantity)}</TableCell>
                                    <TableCell>{formatCurrencyInCents(item.unit_value_cents)}</TableCell>
                                    <TableCell>{formatCurrencyInCents(item.stock_value_cents)}</TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="primary"
                                            onClick={() => navigate(`/admin/edit-replacement-part/${item.id}`)}
                                            aria-label="editar peça"
                                        >
                                            <Edit />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDelete(item.id)}
                                            aria-label="remover peça"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        }) : (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {showArchived
                                            ? 'Nenhuma peça com estoque zerado nos filtros atuais.'
                                            : 'Nenhuma peça com estoque nos filtros atuais.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    mt: 3,
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        setItemsPerPage(event.target.value);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
            <MaintenanceDetailsModal
                open={maintenanceModal.open}
                maintenanceUuid={maintenanceModal.uuid}
                printerName={maintenanceModal.printerName}
                onClose={handleCloseMaintenance}
            />
        </div>
    );
}

export default ListReplacementPartsPage;
